import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores';
import { API_URL, formatCurrency, formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Plus,
  Receipt,
  Camera,
  Upload,
  Loader2,
  ScanLine,
  Check,
  X,
  Trash2,
  Calendar,
  Edit2,
  Link2,
} from 'lucide-react';

const EXPENSE_CATEGORIES = [
  { value: 'material', label: 'Matériel' },
  { value: 'transport', label: 'Transport' },
  { value: 'food', label: 'Restauration' },
  { value: 'service', label: 'Service' },
  { value: 'other', label: 'Autre' },
];

const ExpenseCard = ({ expense, events, onDelete, onLinkEvent }) => {
  const eventName = events?.find((e) => e.id === expense.event_id)?.title;

  return (
    <Card className="card-hover" data-testid={`expense-card-${expense.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-medium">{expense.description || 'Dépense'}</h3>
              {expense.category && (
                <Badge variant="outline">
                  {EXPENSE_CATEGORIES.find((c) => c.value === expense.category)?.label || expense.category}
                </Badge>
              )}
            </div>
            {expense.vendor && (
              <p className="text-sm text-muted-foreground">{expense.vendor}</p>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              {formatDate(expense.date || expense.created_at)}
            </p>
            
            {/* Event link */}
            {eventName ? (
              <div className="mt-2 flex items-center gap-1.5">
                <Link2 className="w-3.5 h-3.5 text-primary" />
                <span className="text-xs text-primary font-medium">{eventName}</span>
              </div>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                className="mt-1 h-7 text-xs text-muted-foreground"
                onClick={() => onLinkEvent(expense)}
                data-testid={`link-event-${expense.id}`}
              >
                <Link2 className="w-3 h-3 mr-1" /> Lier à un événement
              </Button>
            )}

            {/* OCR Data Preview */}
            {expense.ocr_data && Object.keys(expense.ocr_data).length > 0 && (
              <div className="mt-2 p-2 bg-secondary/50 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Données OCR:</p>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(expense.ocr_data).slice(0, 3).map(([key, value]) => (
                    <Badge key={key} variant="outline" className="text-xs">
                      {key}: {String(value).substring(0, 20)}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="text-right">
            <p className="text-lg font-bold">
              {expense.amount ? formatCurrency(expense.amount) : '-'}
            </p>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive mt-2"
              onClick={() => onDelete(expense.id)}
              data-testid={`delete-expense-${expense.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const OCRScannerDialog = ({ open, onOpenChange, onSave, events }) => {
  const { token } = useAuthStore();
  const fileInputRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    date: '',
    category: 'other',
    vendor: '',
    event_id: 'none',
  });

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsProcessing(true);
    setOcrResult(null);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const res = await fetch(`${API_URL}/expenses/ocr`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formDataUpload,
      });

      const data = await res.json();
      setOcrResult(data);

      if (data.success) {
        setFormData((prev) => ({
          ...prev,
          amount: data.amount?.toString() || prev.amount,
          date: data.date || prev.date,
        }));
        toast.success('Image analysée avec succès');
      } else {
        toast.error('Erreur lors de l\'analyse OCR');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSave = () => {
    onSave({
      ...formData,
      amount: parseFloat(formData.amount) || null,
      event_id: formData.event_id === 'none' ? null : formData.event_id,
      ocr_data: ocrResult || {},
    });
    setFormData({
      description: '',
      amount: '',
      date: '',
      category: 'other',
      vendor: '',
      event_id: 'none',
    });
    setOcrResult(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ScanLine className="w-5 h-5" />
            Scanner une dépense
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Upload Zone */}
          <div
            className="border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-primary transition-colors"
            onClick={() => fileInputRef.current?.click()}
            data-testid="ocr-upload-zone"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={handleFileSelect}
            />
            {isProcessing ? (
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="w-10 h-10 animate-spin text-primary" />
                <p>Analyse en cours...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <Camera className="w-8 h-8 text-primary" />
                </div>
                <p className="font-medium">Cliquez pour scanner</p>
                <p className="text-sm text-muted-foreground">
                  ou prenez une photo de votre ticket
                </p>
              </div>
            )}
          </div>

          {/* OCR Result */}
          {ocrResult && (
            <Card className={ocrResult.success ? 'border-green-500/50' : 'border-red-500/50'}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  {ocrResult.success ? (
                    <Check className="w-5 h-5 text-green-500" />
                  ) : (
                    <X className="w-5 h-5 text-red-500" />
                  )}
                  <span className="font-medium">
                    {ocrResult.success ? 'Données extraites' : 'Extraction partielle'}
                  </span>
                </div>
                {ocrResult.amount && (
                  <p className="text-sm">
                    <span className="text-muted-foreground">Montant détecté:</span>{' '}
                    <span className="font-bold">{formatCurrency(ocrResult.amount)}</span>
                  </p>
                )}
                {ocrResult.date && (
                  <p className="text-sm">
                    <span className="text-muted-foreground">Date détectée:</span>{' '}
                    {ocrResult.date}
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Manual Form */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Montant (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                  data-testid="expense-amount"
                />
              </div>
              <div className="space-y-2">
                <Label>Catégorie</Label>
                <Select
                  value={formData.category}
                  onValueChange={(v) => setFormData({ ...formData, category: v })}
                >
                  <SelectTrigger data-testid="expense-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPENSE_CATEGORIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Description de la dépense"
                data-testid="expense-description"
              />
            </div>

            <div className="space-y-2">
              <Label>Fournisseur</Label>
              <Input
                value={formData.vendor}
                onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                placeholder="Nom du fournisseur"
              />
            </div>

            <div className="space-y-2">
              <Label>Lier à un événement</Label>
              <Select
                value={formData.event_id}
                onValueChange={(v) => setFormData({ ...formData, event_id: v })}
              >
                <SelectTrigger data-testid="expense-event-select">
                  <SelectValue placeholder="Aucun événement" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun événement</SelectItem>
                  {events?.map((evt) => (
                    <SelectItem key={evt.id} value={evt.id}>{evt.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button onClick={handleSave} data-testid="save-expense-btn">
              Enregistrer
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ExpensesPage = () => {
  const { token } = useAuthStore();
  const queryClient = useQueryClient();
  const [scannerOpen, setScannerOpen] = useState(false);
  const [linkEventExpense, setLinkEventExpense] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState('none');

  const { data: expenses = [], isLoading } = useQuery({
    queryKey: ['expenses'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/expenses`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch expenses');
      return res.json();
    },
  });

  const { data: events = [] } = useQuery({
    queryKey: ['events'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/events`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch events');
      return res.json();
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/expenses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Failed to create expense');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      toast.success('Dépense enregistrée');
      setScannerOpen(false);
    },
    onError: () => toast.error('Erreur lors de l\'enregistrement'),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const res = await fetch(`${API_URL}/expenses/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to delete expense');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      toast.success('Dépense supprimée');
    },
    onError: () => toast.error('Erreur lors de la suppression'),
  });

  const linkEventMutation = useMutation({
    mutationFn: async ({ expenseId, eventId }) => {
      const res = await fetch(`${API_URL}/expenses/${expenseId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ event_id: eventId }),
      });
      if (!res.ok) throw new Error('Failed to link event');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      toast.success('Dépense liée à l\'événement');
      setLinkEventExpense(null);
    },
    onError: () => toast.error('Erreur lors de la liaison'),
  });

  // Calculate total
  const totalExpenses = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="expenses-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold">Dépenses</h1>
          <p className="text-muted-foreground">Scannez et gérez vos tickets</p>
        </div>
        <Button onClick={() => setScannerOpen(true)} data-testid="scan-expense-btn">
          <Camera className="w-4 h-4 mr-2" /> Scanner
        </Button>
      </div>

      {/* Total Card */}
      <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
        <CardContent className="p-6">
          <p className="text-sm text-muted-foreground mb-1">Total des dépenses</p>
          <p className="text-3xl font-heading font-bold">{formatCurrency(totalExpenses)}</p>
          <p className="text-sm text-muted-foreground mt-1">
            {expenses.length} dépense(s) enregistrée(s)
          </p>
        </CardContent>
      </Card>

      {/* Expenses List */}
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="flex justify-between">
                  <div className="space-y-2">
                    <Skeleton className="h-5 w-32" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                  <Skeleton className="h-8 w-20" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : expenses.length > 0 ? (
        <div className="space-y-3">
          {expenses.map((expense) => (
            <ExpenseCard
              key={expense.id}
              expense={expense}
              events={events}
              onDelete={(id) => {
                if (window.confirm('Supprimer cette dépense ?')) {
                  deleteMutation.mutate(id);
                }
              }}
              onLinkEvent={(exp) => {
                setSelectedEventId(exp.event_id || 'none');
                setLinkEventExpense(exp);
              }}
            />
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Receipt className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-heading font-semibold mb-2">Aucune dépense</h3>
            <p className="text-muted-foreground mb-4">
              Scannez votre premier ticket de caisse
            </p>
            <Button onClick={() => setScannerOpen(true)}>
              <Camera className="w-4 h-4 mr-2" /> Scanner un ticket
            </Button>
          </CardContent>
        </Card>
      )}

      <OCRScannerDialog
        open={scannerOpen}
        onOpenChange={setScannerOpen}
        onSave={(data) => createMutation.mutate(data)}
        events={events}
      />

      {/* Link Event Dialog */}
      <Dialog open={!!linkEventExpense} onOpenChange={(open) => !open && setLinkEventExpense(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Lier à un événement</DialogTitle>
            <DialogDescription>Sélectionnez l'événement à associer à cette dépense</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Select value={selectedEventId} onValueChange={setSelectedEventId}>
              <SelectTrigger data-testid="link-event-select">
                <SelectValue placeholder="Choisir un événement" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucun événement</SelectItem>
                {events.map((evt) => (
                  <SelectItem key={evt.id} value={evt.id}>{evt.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setLinkEventExpense(null)}>Annuler</Button>
              <Button
                onClick={() => {
                  linkEventMutation.mutate({
                    expenseId: linkEventExpense.id,
                    eventId: selectedEventId === 'none' ? null : selectedEventId,
                  });
                }}
                data-testid="confirm-link-event-btn"
              >
                Enregistrer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExpensesPage;
